// AI content reports — what users flagged in-app (Google Play AI-Generated
// Content policy). Users create reports from the app; admins review here and
// move each one to Reviewed / Actioned / Dismissed.
import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, limit, getDocs,
  startAfter, where, doc, updateDoc, getCountFromServer } from 'firebase/firestore';
import { db } from '../firebase';
import { PageHeader, Table, Badge, StatCard,
  GoldBtn, TabButtons, Spinner } from '../components/UI';

const ACCENT = '#008751';
const PAGE_SIZE = 20;

const REASONS = {
  offensive:  { label: 'Offensive or hateful',    icon: '🤬' },
  sexual:     { label: 'Sexual or inappropriate', icon: '🔞' },
  violent:    { label: 'Violent or dangerous',    icon: '⚠️' },
  harassment: { label: 'Harassment or bullying',  icon: '🙅' },
  misleading: { label: 'False or misleading',     icon: '❓' },
  other:      { label: 'Something else',          icon: '🚩' },
};

const SOURCES = {
  chat: 'Chat reply', chat_image: 'Generated image', languages: 'Languages',
  jobs: 'Jobs', support_chat: 'Support chat', voice: 'Voice', voice_handsfree: 'Hands-free voice',
  pet: 'Pet Companion', pet_care: 'Pet care advisor', pet_mood: 'Pet mood scan', pet_sound: 'Pet sound',
};

export default function Reports() {
  const [reports,  setReports]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [lastDoc,  setLastDoc]  = useState(null);
  const [hasMore,  setHasMore]  = useState(true);
  const [filter,   setFilter]   = useState('open');
  const [stats,    setStats]    = useState({});
  const [selected, setSelected] = useState(null);

  useEffect(() => { loadReports(true); }, [filter]);
  useEffect(() => { loadStats(); }, []);

  async function loadStats() {
    try {
      const col = collection(db, 'content_reports');
      const [open, reviewed, actioned, all] = await Promise.all([
        getCountFromServer(query(col, where('status', '==', 'open'))),
        getCountFromServer(query(col, where('status', '==', 'reviewed'))),
        getCountFromServer(query(col, where('status', '==', 'actioned'))),
        getCountFromServer(col),
      ]);
      setStats({
        open:     open.data().count,
        reviewed: reviewed.data().count,
        actioned: actioned.data().count,
        all:      all.data().count,
      });
    } catch (e) { console.error(e); }
  }

  async function loadReports(reset = true) {
    setLoading(true);
    try {
      let q = query(collection(db, 'content_reports'), orderBy('createdAt', 'desc'), limit(PAGE_SIZE));
      if (filter !== 'all') q = query(q, where('status', '==', filter));
      if (!reset && lastDoc) q = query(q, startAfter(lastDoc));
      const snap = await getDocs(q);
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setReports(reset ? data : [...reports, ...data]);
      setLastDoc(snap.docs[snap.docs.length - 1] || null);
      setHasMore(snap.docs.length === PAGE_SIZE);
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  async function updateStatus(id, status) {
    try {
      await updateDoc(doc(db, 'content_reports', id), { status, reviewedAt: new Date() });
      setReports(prev => {
        const next = prev.map(r => r.id === id ? { ...r, status } : r);
        return (filter !== 'all' && filter !== status) ? next.filter(r => r.id !== id) : next;
      });
      loadStats();
      if (selected?.id === id) setSelected(prev => ({ ...prev, status }));
    } catch (e) { alert('Error: ' + e.message); }
  }

  const fmtDate = ts => ts?.toDate
    ? ts.toDate().toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
    : '—';

  const preview = (t, n = 70) => {
    const s = (t || '').replace(/\s+/g, ' ').trim();
    return s.length > n ? s.slice(0, n) + '…' : (s || '—');
  };

  const actions = (row) => (
    <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
      {row.status === 'open' && <GoldBtn small onClick={() => updateStatus(row.id, 'reviewed')}>Reviewed</GoldBtn>}
      {row.status !== 'actioned' && <GoldBtn small danger onClick={() => updateStatus(row.id, 'actioned')}>Actioned</GoldBtn>}
      {row.status !== 'dismissed' && <GoldBtn small onClick={() => updateStatus(row.id, 'dismissed')}>Dismiss</GoldBtn>}
    </div>
  );

  const COLUMNS = [
    { key: 'reason', label: 'Reason',
      render: (v, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(239,68,68,0.1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16,
            border: '1px solid rgba(239,68,68,0.15)', flexShrink: 0 }}>
            {(REASONS[v] || REASONS.other).icon}
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>{(REASONS[v] || REASONS.other).label}</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>{SOURCES[row.source] || row.source || '—'}</div>
          </div>
        </div>
      )},
    { key: 'contentText', label: 'Flagged content',
      render: (v, row) => <span style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.7)' }}>{row.imageUrl ? '🖼 ' : ''}{preview(v)}</span> },
    { key: 'platform',  label: 'Platform', render: (v, row) => `${v || '—'} ${row.appVersion || ''}` },
    { key: 'status',    label: 'Status',   render: v => <Badge label={v} color={v} /> },
    { key: 'createdAt', label: 'Time',     render: fmtDate },
    { key: 'id',        label: 'Actions',  render: (v, row) => actions(row) },
  ];

  const STATUS_TABS = [
    { value: 'open',      label: 'Open',      count: stats.open || 0 },
    { value: 'reviewed',  label: 'Reviewed',  count: stats.reviewed || 0 },
    { value: 'actioned',  label: 'Actioned',  count: stats.actioned || 0 },
    { value: 'dismissed', label: 'Dismissed' },
    { value: 'all',       label: 'All' },
  ];

  return (
    <div style={{ padding: 32 }}>
      <PageHeader title="AI Content Reports"
        subtitle="What users flagged in the app — review every report (Google Play AI-Generated Content policy)" />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 16, marginBottom: 24 }}>
        <StatCard label="Open Reports" value={stats.open || 0} icon="🚩" color="#ef4444" borderColor="#ef4444"
          sub={stats.open > 0 ? 'Needs review' : 'All clear'} />
        <StatCard label="Reviewed" value={stats.reviewed || 0} icon="👁" color="#f97316" borderColor="#f97316" />
        <StatCard label="Actioned" value={stats.actioned || 0} icon="✅" color="#a855f7" borderColor="#a855f7" />
        <StatCard label="All Time" value={stats.all || 0} icon="📋" color={ACCENT} borderColor={ACCENT} />
      </div>

      <TabButtons tabs={STATUS_TABS} active={filter} onChange={setFilter} />

      <div style={{ background: '#1A2332', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 16, overflow: 'hidden', marginBottom: 16 }}>
        <Table columns={COLUMNS} data={reports} loading={loading && reports.length === 0} onRowClick={setSelected} />
      </div>

      {hasMore && !loading && (
        <div style={{ textAlign: 'center' }}><GoldBtn onClick={() => loadReports(false)}>Load More</GoldBtn></div>
      )}
      {loading && reports.length > 0 && <Spinner />}

      {selected && (
        <div onClick={() => setSelected(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(6px)' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#1A2332', border: '1px solid rgba(239,68,68,0.2)',
            borderRadius: 20, width: 600, maxWidth: '92vw', maxHeight: '85vh', overflow: 'auto', padding: 32,
            boxShadow: '0 24px 80px rgba(0,0,0,0.5)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
              <div>
                <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: 17 }}>
                  {(REASONS[selected.reason] || REASONS.other).icon} {(REASONS[selected.reason] || REASONS.other).label}
                </div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', marginTop: 4 }}>
                  {SOURCES[selected.source] || selected.source} · {fmtDate(selected.createdAt)}
                </div>
              </div>
              <button onClick={() => setSelected(null)} style={{ background: 'rgba(255,255,255,0.05)', border: 'none',
                color: 'rgba(255,255,255,0.4)', fontSize: 18, cursor: 'pointer', width: 36, height: 36, borderRadius: 10 }}>✕</button>
            </div>

            <div style={{ marginBottom: 18 }}><Badge label={selected.status} color={selected.status} /></div>

            {selected.imageUrl && (
              <img src={selected.imageUrl} alt="Reported" style={{ width: '100%', borderRadius: 12, marginBottom: 16 }} />
            )}
            {selected.contentText && (
              <div style={{ padding: 14, background: '#0F1419', borderRadius: 12, fontSize: 13, lineHeight: 1.55,
                whiteSpace: 'pre-wrap', color: 'rgba(255,255,255,0.8)', marginBottom: 16, border: '1px solid rgba(255,255,255,0.05)' }}>
                {selected.contentText}
              </div>
            )}
            {selected.note && (
              <div style={{ padding: 14, background: 'rgba(212,175,55,0.06)', borderRadius: 12, fontSize: 13,
                color: 'rgba(255,255,255,0.8)', marginBottom: 16, border: '1px solid rgba(212,175,55,0.15)' }}>
                <div style={{ fontSize: 11, color: '#D4AF37', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>User's note</div>
                {selected.note}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
              {[
                ['Reporter UID', selected.uid],
                ['Conversation', selected.conversationId || '—'],
                ['Message',      selected.messageId || '—'],
                ['Platform',     `${selected.platform || '—'} ${selected.appVersion || ''}`],
              ].map(([label, val]) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '11px 14px',
                  background: '#0F1419', borderRadius: 10, border: '1px solid rgba(255,255,255,0.04)' }}>
                  <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: 1 }}>{label}</span>
                  <span style={{ fontSize: 13, fontFamily: 'DM Mono', color: 'rgba(255,255,255,0.8)', wordBreak: 'break-all',
                    maxWidth: 340, textAlign: 'right' }}>{String(val || '—')}</span>
                </div>
              ))}
            </div>

            {actions(selected)}
          </div>
        </div>
      )}
    </div>
  );
}
